import type {AxiosResponse} from "axios";

import CoreApi from "@/src/modules/core/api";
import {axios} from "@/src/modules/core/lib";
import type {GetListResponse, GetResponse} from "@/src/modules/core/types";

const PATH = "directmessage";

type ThreadListParams = {
  box?: "inbox" | "sent";
  unread?: boolean;
  page?: {limit: number; offset: number};
};

type RecipientsParams = {
  property_id?: number;
  recipient_type?: "staff" | "resident";
  search?: string;
  limit?: number;
};

function getThreadListParams(params?: ThreadListParams) {
  const searchParams = new URLSearchParams();
  if (params?.box) {
    searchParams.append("box", params.box);
  }
  if (params?.unread) {
    searchParams.append("unread", "1");
  }
  if (params?.page) {
    searchParams.append("page[limit]", String(params.page.limit));
    searchParams.append("page[offset]", String(params.page.offset));
  }
  return searchParams;
}

function getRecipientsParams(params?: RecipientsParams) {
  const searchParams = new URLSearchParams();
  if (params?.property_id != null) {
    searchParams.append("property_id", String(params.property_id));
  }
  if (params?.recipient_type) {
    searchParams.append("recipient_type", params.recipient_type);
  }
  const search = params?.search?.trim();
  if (search) {
    searchParams.append("search", search);
  }
  if (params?.limit != null) {
    searchParams.append("limit", String(params.limit));
  }
  return searchParams;
}

class DirectMessagesApi extends CoreApi {
  getRecipients(
    params?: RecipientsParams,
  ): Promise<AxiosResponse<GetListResponse<unknown>>> {
    return axios({
      url: `${PATH}/recipients`,
      method: "get",
      params: getRecipientsParams(params),
    });
  }

  createThread(
    data: Record<string, unknown>,
  ): Promise<AxiosResponse<GetResponse<unknown>>> {
    return axios({
      url: `${PATH}/threads`,
      method: "post",
      data,
    });
  }

  getThreads(
    params?: ThreadListParams,
  ): Promise<AxiosResponse<GetListResponse<unknown>>> {
    return axios({
      url: `${PATH}/threads`,
      method: "get",
      params: getThreadListParams(params),
    });
  }

  getThread(id: number): Promise<AxiosResponse<GetResponse<unknown>>> {
    return axios({
      url: `${PATH}/threads/${id}`,
      method: "get",
    });
  }

  createPost(
    threadId: number,
    data: Record<string, unknown>,
  ): Promise<AxiosResponse<GetResponse<unknown>>> {
    return axios({
      url: `${PATH}/threads/${threadId}/posts`,
      method: "post",
      data,
    });
  }

  updateThread(
    threadId: number,
    data: Record<string, unknown>,
  ): Promise<AxiosResponse<GetResponse<unknown>>> {
    return axios({
      url: `${PATH}/threads/${threadId}`,
      method: "patch",
      data,
    });
  }

  markThreadRead(threadId: number): Promise<AxiosResponse<GetResponse<unknown>>> {
    return axios({
      url: `${PATH}/threads/${threadId}/read`,
      method: "post",
    });
  }
}

export const directMessagesApi = new DirectMessagesApi();
